<template>
  <div class="master-repair-error-codes">
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Danh mục mã lỗi'">
          <template v-slot:toolbar>
            <div class="d-flex">
              <button
                type="button"
                v-if="canManage"
                class="btn btn-primary font-weight-bolder btn-sm"
                @click="openAddModal"
              >
                <i style="font-size: 1rem" class="flaticon2-add-1"></i>
                Tạo mới
              </button>
            </div>
          </template>

          <template v-slot:preview>
            <!-- Filter -->
            <div class="col-md-12 mb-4">
              <b-form @submit.prevent="onSearch">
                <div class="row">
                  <b-input
                    size="sm"
                    placeholder="Tìm theo mã lỗi hoặc tên lỗi..."
                    v-model.trim="search"
                    class="col-md-4"
                    @input="onInputChange"
                    v-on:keyup.enter="onSearch"
                  ></b-input>
                </div>
              </b-form>
            </div>

            <!-- Table -->
            <b-table
              :items="rows"
              :fields="fields"
              :busy="loading"
              bordered
              hover
              show-empty
              empty-text="Không có dữ liệu"
              class="table-vertical-center"
            >
              <template #table-busy>
                <div class="text-center text-primary my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>Đang tải...</strong>
                </div>
              </template>

              <template #cell(stt)="data">
                {{ (page - 1) * limit + data.index + 1 }}
              </template>

              <template #cell(actions)="data">
                <div class="d-flex justify-content-center">
                  <button
                    v-if="canManage"
                    class="btn btn-icon btn-sm btn-outline-primary mr-2"
                    @click="openEditModal(data.item)"
                    title="Sửa"
                  >
                    <i class="flaticon-edit-1 p-0"></i>
                  </button>
                  <button
                    v-if="canManage"
                    class="btn btn-icon btn-sm btn-outline-danger"
                    @click="confirmDelete(data.item)"
                    title="Xoá"
                  >
                    <i class="flaticon2-rubbish-bin-delete-button p-0"></i>
                  </button>
                </div>
              </template>
            </b-table>

            <!-- Pagination -->
            <b-row class="mt-3">
              <b-col>
                <p class="text-dark" style="font-weight: 500">
                  Tổng số mã lỗi: {{ totalRow }}
                </p>
              </b-col>
              <b-col>
                <b-pagination
                  v-show="totalPage >= 2"
                  v-model="page"
                  :total-rows="totalRow"
                  :per-page="limit"
                  align="right"
                  size="sm"
                  @change="onPageChange"
                ></b-pagination>
              </b-col>
            </b-row>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- Modal Add/Edit -->
    <b-modal
      id="modal-error-code-form"
      :title="isEdit ? 'Sửa mã lỗi' : 'Tạo mã lỗi'"
      hide-footer
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <b-form @submit.prevent="onSubmit">
        <b-form-group label-for="input-error-code">
          <template v-slot:label>
            Mã lỗi <span class="text-danger">*</span>
          </template>
          <b-form-input
            id="input-error-code"
            v-model.trim="$v.form.errorCode.$model"
            :state="validateState('errorCode')"
            placeholder="VD: E001"
            size="sm"
          ></b-form-input>
          <b-form-invalid-feedback>
            Mã lỗi là bắt buộc và tối đa 50 ký tự.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label-for="input-error-name">
          <template v-slot:label>
            Tên lỗi <span class="text-danger">*</span>
          </template>
          <b-form-input
            id="input-error-name"
            v-model.trim="$v.form.errorName.$model"
            :state="validateState('errorName')"
            placeholder="VD: Lỗi màn hình cảm ứng"
            size="sm"
          ></b-form-input>
          <b-form-invalid-feedback>
            Tên lỗi là bắt buộc và tối đa 255 ký tự.
          </b-form-invalid-feedback>
        </b-form-group>

        <div class="d-flex justify-content-end mt-4">
          <b-button
            variant="secondary"
            size="sm"
            class="mr-2"
            @click="$bvModal.hide('modal-error-code-form')"
          >
            Hủy
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            size="sm"
            :disabled="submitting"
          >
            {{ submitting ? 'Đang lưu...' : 'Lưu' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { validationMixin } from 'vuelidate';
import { required, maxLength } from 'vuelidate/lib/validators';
import debounce from 'debounce';
import SavaDataUtil from '@/utils/saveDataToLocal';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import { extractError } from '@/utils/warranty-service.helpers';

const API_BASE = 'v2/warranty-service/repair-error-codes';

export default {
  name: 'MasterRepairErrorCodes',
  mixins: [validationMixin],
  components: { KTCodePreview },
  data() {
    return {
      rows: [],
      loading: false,
      search: '',
      page: 1,
      limit: 20,
      totalRow: 0,
      totalPage: 0,
      fields: [
        { key: 'stt', label: 'STT', tdClass: 'text-center', thStyle: { width: '8%' } },
        { key: 'errorCode', label: 'Mã lỗi', thStyle: { width: '20%' } },
        { key: 'errorName', label: 'Tên lỗi' },
        {
          key: 'actions',
          label: 'Thao tác',
          tdClass: 'text-center',
          thStyle: { width: '15%' },
        },
      ],
      // Form
      isEdit: false,
      submitting: false,
      form: {
        id: null,
        errorCode: '',
        errorName: '',
      },
    };
  },
  validations: {
    form: {
      errorCode: { required, maxLength: maxLength(50) },
      errorName: { required, maxLength: maxLength(255) },
    },
  },
  computed: {
    canManage() {
      return SavaDataUtil.checkPermission('WARRANTY_SERVICE_MANAGE_MASTER');
    },
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Dịch vụ bảo hành' },
      { title: 'Danh mục mã lỗi' },
    ]);
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await ApiService.query(API_BASE, {
          params: { search: this.search, page: this.page, limit: this.limit },
        });
        const payload = data.data || {};
        this.rows = payload.dataset || [];
        this.totalRow = payload.totalRow || 0;
        this.totalPage = payload.totalPage || 0;
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.fetchData();
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.$nextTick(() => this.fetchData());
    },
    debouncedSearch: debounce(function () {
      this.onSearch();
    }, 400),
    onInputChange() {
      this.debouncedSearch();
    },

    // ---- Form Add/Edit ----
    openAddModal() {
      this.isEdit = false;
      this.resetForm();
      this.$bvModal.show('modal-error-code-form');
    },
    openEditModal(item) {
      this.isEdit = true;
      this.form = {
        id: item.id,
        errorCode: item.errorCode,
        errorName: item.errorName,
      };
      this.$nextTick(() => {
        this.$v.form.$reset();
        this.$bvModal.show('modal-error-code-form');
      });
    },
    resetForm() {
      this.form = { id: null, errorCode: '', errorName: '' };
      this.$v.form.$reset();
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    async onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) return;

      this.submitting = true;
      try {
        if (this.isEdit) {
          await ApiService.put(API_BASE, {
            id: this.form.id,
            errorCode: this.form.errorCode,
            errorName: this.form.errorName,
          });
          makeToastSuccess('Cập nhật mã lỗi thành công');
        } else {
          await ApiService.post(API_BASE, {
            errorCode: this.form.errorCode,
            errorName: this.form.errorName,
          });
          makeToastSuccess('Tạo mã lỗi thành công');
        }
        this.$bvModal.hide('modal-error-code-form');
        this.fetchData();
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.submitting = false;
      }
    },

    // ---- Delete ----
    async confirmDelete(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Xoá mã lỗi "${item.errorCode} - ${item.errorName}"?`,
        {
          title: 'Xác nhận xoá',
          okTitle: 'Xoá',
          cancelTitle: 'Huỷ',
          okVariant: 'danger',
          centered: true,
        },
      );
      if (!ok) return;

      try {
        await ApiService.delete(`${API_BASE}/${item.id}`);
        makeToastSuccess('Đã xoá mã lỗi');
        this.fetchData();
      } catch (err) {
        makeToastFaile(extractError(err));
      }
    },

  },
};
</script>

<style lang="scss" scoped>
.master-repair-error-codes {
  ::v-deep .table-vertical-center td {
    vertical-align: middle;
  }
}
</style>
